import React, { useContext } from 'react'
import DOMPurify from 'dompurify'
import { TextContext } from 'hocs'
import {
  BookmarkToggle,
  Button,
  ContactPerson,
  Container,
  Grid,
  Icon,
  OpeningIndicator,
  Tooltip,
} from 'components/atoms'
import PropTypes from 'prop-types'
import * as Styled from './GeneralInformation.styles'
import {
  BOOKABLE_TYPES as RESULT_TYPES,
  entryPointId,
} from 'settings/app/constants'
import { Display4 } from 'components/atoms/IcoMoon'
import { scrollToElement } from 'settings/app/helpers'
import { DETAIL_HEADER_HAS_CONTRIBUTION } from 'containers/Detail/helpers/constants'
import { useCostEstimate } from 'hocs/CostEstimate/CostEstimate'

/**
 * component to show general information's
 * @returns {*}
 * @constructor
 */
const GeneralInformation = (props) => {
  const {
    data,
    type,
    isBookmarked,
    onToggleBookmark,
    onShowOverlay,
    scrollToElements,
    showCostCalculationButton,
    isTreatmentReason,
  } = props
  const textContext = useContext(TextContext)
  const { handleCostEstimate } = useCostEstimate()

  const hasValidContribution =
    DETAIL_HEADER_HAS_CONTRIBUTION.includes(type) &&
    data.details?.contribution &&
    Array.isArray(data.details.contribution) &&
    data.details.contribution.length > 0

  const showTransparency =
    data.details?.transparencyReport?.rating &&
    (type === entryPointId.CARESERVICES || type === entryPointId.CAREHOMES)

  const HAS_CONTACT_PERSON = [
    entryPointId.CAREHOMES,
    entryPointId.CARESERVICES,
    entryPointId.DAYANDNIGHTCARE,
  ]

  // https://bugs.onedev.aok.de/browse/MGMPMNAV-3539
  const SKIP_INFO_LIST = type === entryPointId.HOSPITALS && !isTreatmentReason

  const getInfoTitleByType = () => {
    if (!data) return

    return textContext[`generalInformation.${ type }`]
  }

  const getTitleByType = () => {
    if (!data) return

    return type === entryPointId.HOSPITALS ? data.locationName : data.name
  }

  const getLastUpdateInfo = () => (
    <Grid
      item
      xs={ 12 }
      sm={ 6 }
      print={ 6 }
      className={ 'inner-grid general-info-footer' }>
      <Styled.LastUpdate type={ type }>
        {textContext['general.lastUpdate']} {data.lastUpdate}
      </Styled.LastUpdate>
    </Grid>
  )

  /**
   * get mapped contact person
   * condition (name, email, phone) have to provided
   * @returns {JSX.Element|boolean}
   */
  const getContactPersons = () => {
    const { contactPersons } = data
    const contactPersonHTML = []

    contactPersons.forEach((contactPerson, key) => {
      if (
        !contactPerson?.name ||
        !contactPerson?.email ||
        !contactPerson?.phone
      ) {
        return true
      }

      // MGMPMNAV-3609
      delete contactPerson.phone
      delete contactPerson.email

      contactPersonHTML.push(
        <ContactPerson key={ key } contactPerson={ contactPerson } />
      )
    })

    return !!contactPersonHTML.length ? (
      <div className='contact-persons'>
        <p data-cy='contact-persons-title'>
          <b>{textContext['generalInformation.contactPersons']}</b>
        </p>
        {contactPersonHTML}
      </div>
    ) : (
      false
    )
  }

  /**
   * filter scrollToElements by current link list label and return css target for scroll event
   * @param linkListLabel
   * @returns {*|boolean}
   */
  const filterForLinkListScrollElementsByLabel = (linkListLabel) => {
    const currentScrollElement = scrollToElements.find((item) =>
      item.searchFor.find((sFString) => linkListLabel.includes(sFString)) !==
      undefined
        ? item
        : false
    )

    return typeof currentScrollElement !== 'undefined' &&
      Object.keys(currentScrollElement).length > 0
      ? currentScrollElement
      : false
  }

  const getNonLinkedLinkListElement = (linkListLabel) => (
    <span dangerouslySetInnerHTML={{ __html: linkListLabel }} />
  )

  // check if label is part of scroll elements
  const getLinkListItem = (linkListLabel) => {
    const currentScrollElement =
      filterForLinkListScrollElementsByLabel(linkListLabel)

    return !currentScrollElement ? (
      getNonLinkedLinkListElement(linkListLabel)
    ) : (
      <Styled.QsrLink
        dangerouslySetInnerHTML={{
          __html: currentScrollElement?.newLabel
            ? currentScrollElement.newLabel
            : linkListLabel,
        }}
        onClick={ () => scrollToElement(currentScrollElement?.scrollTo, true) }
      />
    )
  }

  const getTransparencyReport = () => {
    const { transparencyReport } = data.details
    const hasValidTooltip =
      transparencyReport?.tooltip &&
      transparencyReport.tooltip?.tip &&
      transparencyReport.tooltip?.text

    return (
      <ul className={ 'list-checkmark list-transparency-report' }>
        <li>
          <div>
            {textContext['generalInformation.transparencyReport']}
            <strong> {transparencyReport.rating} </strong>
            {textContext['generalInformation.transparencyReport.addLabel']}
            {hasValidTooltip && (
              <Tooltip
                dialogTitle={ transparencyReport.tooltip.title }
                infoIconId='transparenz-report-icon-id'
                tooltipId='transparenz-report-tooltip-id'
                className='transparencyReport-tooltip'
                arrow='left'>
                {transparencyReport.tooltip.text}
              </Tooltip>
            )}
          </div>
        </li>
      </ul>
    )
  }

  const getContribution = () => (
    <ul className={ 'list-checkmark list-contribution' }>
      {data.details.contribution.map((contrib, index) => {
        let contribStr
        let contribStrIsSplit = false

        switch (contrib.type) {
          case 'contributionDay':
          case 'contributionNight':
            contribStr = textContext['generalInformation.dayAndNightCare']
              .replace(
                '__SEARCH__TERM__TYPE__',
                textContext[
                  `generalInformation.dayAndNightCare.${ contrib.type }`
                ]
              )
              .replace('__SEARCH__TERM__DAYS__', contrib.days)
            if (contrib.days === 1) {
              contribStr = contribStr.replace('Besuchstagen', 'Besuchstag')
            }
            break

          case 'personalContribution':
          case 'personalContributionShortTerm':
            contribStr = textContext[`generalInformation.${ contrib.type }`]
              .replace('__SEARCH__CARE__LEVEL__', contrib.careLevel)
              .split('__SEARCH__PRICE__')
            contribStrIsSplit = true
            break
        }

        return (
          <li key={ index }>
            <div>
              {contribStrIsSplit ? (
                <span
                  className='contribution'
                  data-cy={ `contribution-${ index }` }>
                  {contribStr[0]}
                  <strong> {contrib.amount}</strong>
                  {contribStr[1]}
                </span>
              ) : (
                <span
                  className='contribution'
                  data-cy={ `contribution-${ index }` }>
                  {contribStr}
                  <strong> {contrib.amount}</strong>
                </span>
              )}
              {contrib.tooltip && (
                <Tooltip
                  dialogTitle={ contrib.tooltip.title }
                  infoIconId={ `${ contrib.type }-icon-id${ index }` }
                  tooltipId={ `${ contrib.type }-tip-id${ index }` }
                  arrow='left'>
                  {contrib.tooltip.text}
                </Tooltip>
              )}
            </div>
          </li>
        )
      })}
    </ul>
  )

  return (
    <Styled.GeneralInformation
      className={ 'print-border' }
      data-cy-detail-item='Allgemein'>
      {data && (
        <>
          <Container className={ 'container-content-alternative' }>
            <div className={ 'headline-container' }>
              {data.subject && <p className={ 'subject' }>{data.subject}</p>}

              <h2
                data-cy='detail-name'
                className={ `h2 headline-alternative ${
                  data.specification ? 'has-specification' : ''
                }` }
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(getTitleByType()),
                }}
              />

              {data.specification && (
                <h6 className={ 'h6' }>{data.specification}</h6>
              )}

              <BookmarkToggle
                className={ 'bookmark-general-information' }
                selected={ isBookmarked }
                onToggle={ onToggleBookmark }
              />
            </div>

            {/* LEFT HEADER ROW */}
            <Grid container>
              <Grid
                item
                xs={ 12 }
                sm={ 6 }
                print={ 6 }
                className={ 'container-spacing inner-grid' }
                data-cy={ 'list-general-info' }>
                {/* special info */}
                {data.specialInfos &&
                  data.specialInfos.map((info, index) => (
                    <p key={ index }>
                      <Styled.ContactIcon>
                        <Icon className={ `icon-f-${ info.type }` } />
                      </Styled.ContactIcon>
                      {info.message}
                      {info.tooltip && (
                        <span className={ 'general-info-tooltip' }>
                          <Tooltip
                            dialogTitle={ info.message }
                            infoIconId='info-icon'
                            tooltipId='general-info-tooltip'>
                            {info.tooltip}
                          </Tooltip>
                        </span>
                      )}
                    </p>
                  ))}

                {/* address */}
                {data.address && (
                  <p>
                    <Styled.ContactIcon>
                      <Icon className='icon-f-home' />
                    </Styled.ContactIcon>
                    {data.address.street.trim()}
                    {data.address.streetAddition &&
                    data.address.streetAddition !== ''
                      ? ` ${ data.address.streetAddition }`
                      : ''}
                    , {data.address.zip} {data.address.city}
                  </p>
                )}

                {/* phone */}
                {data.phone && (
                  <p>
                    <Styled.ContactIcon>
                      <Icon className='icon-f-phone-handset' />
                    </Styled.ContactIcon>
                    {/* <a id="header-phone" href={ `tel:${ data.phone }` }>{ data.phone }</a> */}
                    <span id='header-phone'>{data.phone}</span>
                  </p>
                )}

                {/* fax */}
                {data.fax && (
                  <p>
                    <Styled.ContactIcon>
                      <Icon className='icon-f-printer' />
                    </Styled.ContactIcon>
                    {data.fax}
                  </p>
                )}

                {/* email */}
                {data.email && (
                  <p>
                    <Styled.ContactIcon>
                      <Icon className='icon-f-envelope' />
                    </Styled.ContactIcon>
                    <a
                      href={ `mailto:${ data.email }` }
                      target='_blank'
                      rel='noopener noreferrer'>
                      {data.email}
                    </a>
                  </p>
                )}

                {/* url */}
                {data.website && (
                  <p>
                    <Styled.ContactIcon>
                      <Display4 />
                    </Styled.ContactIcon>
                    <a
                      href={ `${
                        data.website.match(/^(http)s?:\/\/.*/) ? '' : 'http://'
                      }${ data.website }` }
                      target='_blank'
                      rel='noopener noreferrer'>
                      {data.website}
                    </a>
                  </p>
                )}

                {/* contactPersons */}
                {HAS_CONTACT_PERSON.includes(type) &&
                  !!data.contactPersons &&
                  data.contactPersons.length > 0 &&
                  getContactPersons()}
              </Grid>

              {/* RIGHT HEADER ROW */}
              {!SKIP_INFO_LIST && (
                <Grid
                  item
                  xs={ 12 }
                  sm={ 6 }
                  print={ 6 }
                  className={ 'container-spacing inner-grid' }
                  data-cy='list-checkmark'>
                  {/* info list */}
                  {data.infos && Object.keys(data.infos).length > 0 && (
                    <>
                      <p>
                        <b>{getInfoTitleByType()}</b>
                      </p>

                      {showTransparency && getTransparencyReport()}

                      {data.infos.length > 0 && (
                        <ul className={ 'list-checkmark' }>
                          {Object.keys(data.infos).map((item, index) => {
                            const listItem = data.infos[index]

                            return (
                              <li key={ index }>
                                <span>
                                  {!scrollToElements
                                    ? getNonLinkedLinkListElement(
                                        listItem.label
                                      )
                                    : getLinkListItem(listItem.label)}

                                  {listItem.tooltip && listItem.tooltipTitle && (
                                    <Tooltip
                                      className='tooltip'
                                      dialogTitle={ listItem.tooltipTitle }
                                      infoIconId='info-icon'
                                      tooltipId='general-info-tooltip'>
                                      {listItem.tooltip}
                                    </Tooltip>
                                  )}
                                </span>
                              </li>
                            )
                          })}
                        </ul>
                      )}
                    </>
                  )}

                  {type === entryPointId.CARESUPPORT &&
                    !!data.details?.pricePerHour && (
                      <div className='price-per-hour'>
                        Preis: {data.details.pricePerHour}
                      </div>
                    )}

                  {hasValidContribution && getContribution()}
                </Grid>
              )}
            </Grid>

            {data.treatments && (
              <div
                className={ 'container-spacing inner-grid' }
                data-cy={ 'general-info-treatment-cases' }>
                <p>
                  <b>
                    {textContext['hospital.treatments']} ({data.treatments.year}
                    ):
                  </b>
                  {data.treatments.tooltip && (
                    <Styled.TooltipContainer>
                      <Tooltip
                        infoIconId={ 'info-icon' }
                        tooltipId={ 'hospital-treatment-tooltip' }>
                        {data.treatments.tooltip}
                      </Tooltip>
                    </Styled.TooltipContainer>
                  )}
                </p>
                <p>
                  <b>{data.treatments.cases}</b>&nbsp;({data.treatments.score})
                </p>
              </div>
            )}

            <Grid container>
              {/* "footer" for type hospital */}
              {type === entryPointId.HOSPITALS && (
                <>
                  <Grid
                    item
                    xs={ 12 }
                    sm={ 6 }
                    print={ 6 }
                    className={ 'inner-grid general-info-footer' }>
                    {data.treatments && (
                      <div
                        className='link more-link'
                        onClick={ () => {
                          onShowOverlay()
                        } }>
                        {textContext['hospital.treatments.department']}
                      </div>
                    )}
                  </Grid>
                  {getLastUpdateInfo()}
                </>
              )}

              {/* "footer" for type doctors */}
              {type === entryPointId.DOCTORS && (
                <>
                  {getLastUpdateInfo()}
                  <Grid
                    item
                    xs={ 12 }
                    sm={ 6 }
                    print={ 6 }
                    className={ 'inner-grid general-info-footer' }>
                    {data.openingIndicator && (
                      <OpeningIndicator
                        type={ data.openingIndicator.state }
                        untilMsg={ data.openingIndicator.message }
                        className={ 'general-info-opening' }
                      />
                    )}
                    {data.appointmentUrl && (
                      <Button
                        id={ 'appointment-btn' }
                        text={ textContext['general.makeAnAppointment'] }
                        onClick={ () =>
                          window.open(data.appointmentUrl, '_blank')
                        }
                        className={ 'with-border' }
                        textFirst={ true }
                        icon={ <Icon className='icon-f-new-tab' /> }
                      />
                    )}
                  </Grid>
                </>
              )}

              {/* footer for type carehome/careservice*/}
              {type === entryPointId.CARESERVICES && (
                <>
                  {data?.lastUpdate ? (
                    getLastUpdateInfo()
                  ) : (
                    <Grid
                      item
                      xs={ 12 }
                      sm={ 6 }
                      print={ 6 }
                      className={ 'inner-grid general-info-footer' }
                    />
                  )}
                  {showCostCalculationButton && data?.careCalculationId ? (
                    <Grid item xs={ 12 } sm={ 6 } print={ 6 }>
                      <Styled.CostEstimateWrapper>
                        <Button
                          id={ 'cost-calculation-btn' }
                          className='with-border'
                          text={
                            textContext[
                              'generalInformation.costCalculationButton'
                            ]
                          }
                          onClick={ () =>
                            handleCostEstimate(data?.careCalculationId)
                          }
                        />
                      </Styled.CostEstimateWrapper>
                    </Grid>
                  ) : (
                    <Grid item xs={ 12 } sm={ 6 } print={ 6 } />
                  )}
                </>
              )}

              {/* footer for type carehome/careservice*/}
              {(type === entryPointId.CAREHOMES ||
                type === entryPointId.DAYANDNIGHTCARE ||
                type === entryPointId.CARESUPPORT) &&
                data.lastUpdate && (
                  <>
                    <Grid item xs={ 12 } sm={ 6 } print={ 6 } />
                    {getLastUpdateInfo()}
                  </>
                )}
            </Grid>
          </Container>
        </>
      )}
    </Styled.GeneralInformation>
  )
}

GeneralInformation.defaultProps = {
  data: null,
  type: entryPointId.HOSPITALS,
  isBookmarked: false,
  onToggleBookmark: () => {},
  onShowOverlay: () => {},
  showCostCalculationButton: false,
  showRightColumn: true,
}

GeneralInformation.propTypes = {
  data: PropTypes.shape({
    // ikNumber: PropTypes.string,
    name: PropTypes.string,
    subject: PropTypes.string,
    specification: PropTypes.string,
    street: PropTypes.string,
    zip_code: PropTypes.string,
    city: PropTypes.string,
    phone: PropTypes.string,
    fax: PropTypes.string,
    email: PropTypes.string,
    website: PropTypes.string,
    contactPersons: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        phone: PropTypes.string,
        email: PropTypes.string,
      })
    ),
    infos: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        tooltip: PropTypes.string,
        tooltipTitle: PropTypes.string,
      })
    ),
    treatments: PropTypes.shape({
      year: PropTypes.number,
      cases: PropTypes.number,
      score: PropTypes.string,
      tooltip: PropTypes.string,
    }),
    lastUpdate: PropTypes.string,
    openingIndicator: PropTypes.shape({
      state: PropTypes.string,
      message: PropTypes.string,
    }),
    appointmentUrl: PropTypes.string,
    specialInfos: PropTypes.arrayOf(
      PropTypes.shape({
        message: PropTypes.string,
        tooltip: PropTypes.string,
        type: PropTypes.string,
      })
    ),
    contribution: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf([
          'personalContribution',
          'personalContributionShortTerm',
        ]).isRequired,
        amount: PropTypes.string.isRequired,
        careLevel: PropTypes.string,
        tooltip: PropTypes.shape({
          tip: PropTypes.string,
          text: PropTypes.string,
        }),
      })
    ),
    transparencyReport: PropTypes.shape({
      rating: PropTypes.string,
      tooltip: PropTypes.shape({
        tip: PropTypes.string,
        text: PropTypes.string,
      }),
    }),
  }),
  onToggleBookmark: PropTypes.func,
  isBookmarked: PropTypes.bool,
  onShowOverlay: PropTypes.func,
  type: PropTypes.oneOf([...RESULT_TYPES]),
  scrollToElements: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.arrayOf(
      PropTypes.shape({
        scrollTo: PropTypes.string,
        searchFor: PropTypes.arrayOf(PropTypes.string),
      })
    ),
  ]),
  showCostCalculationButton: PropTypes.bool,
  isTreatmentReason: PropTypes.bool,
}

export default GeneralInformation
